/** @prettier */

/**
 * 通信確認ダイアログ
 */

import {useState, useEffect} from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Paper,
  Grid,
  Stack,
} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

//MTH component import
import APIErrorMessage from '../../component/APIErrorMessage';
//API import
import {
  postCommunicationGateway,
  getCommunicationGateway,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';

import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

import {limit} from '../../define';

/**
 * 通信確認ダイアログ
 * @param props props
 * - gwID ゲートウェイID
 * - isOpenDialog ダイアログ表示フラグ
 * - setIsOpenDialog openのセット関数
 * - restCommunicationTime 通信テストの残り時間
 * - updateComTestStartTime 通信テスト開始時間の更新用関数
 * @returns JSX
 */
const CommunicationTest = (props: {
  gwID: string;
  isOpenDialog: boolean;
  setIsOpenDialog: (arg0: boolean) => void;
  restCommunicationTime: number;
  updateComTestStartTime: (gwID: string) => void;
}) => {
  //パスパラメータ取得
  const {siteID} = useParams();
  const [now, setNow] = useState(new Date()); //今日の日付
  const [errorOpen, setErrorOpen] = useState(false); //APIエラー時エラーメッセージ表示
  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ
  const [isComTestNow, setIsComTestNow] = useState(
    props.restCommunicationTime > 0
  ); // 通信テスト判定
  const [connectionInfo, setConnectionInfo] = useState<
    | apiIF.resParameters.slUnitCommunications
    | apiIF.resParameters.rtUnitCommunications
  >(); // 通信状況

  const trans = useTranslation().t; //翻訳関数

  // 通信状況リスト XS指定用 以下の変数の合計は12にしてください
  const gridXsUnitName = 5;
  const gridXsAddress = 2;
  const gridXsConnectionStatus = 5;

  // ページ更新時に通信テスト中なら通信状況を取得する
  useEffect(() => {
    if (siteID === undefined || isComTestNow === false) {
      return;
    }
    setAPIWait(true);
    getCommunicationGateway(siteID, props.gwID)
      .then(response => {
        setConnectionInfo(response);
      })
      .catch(() => {
        alert(trans('CommunicationTestDialog.alertMsg.operationErr'));
      })
      .finally(() => {
        setAPIWait(false);
      });
  }, [siteID, props.gwID, trans]);

  // 通信テスト判定
  useEffect(() => {
    setIsComTestNow(props.restCommunicationTime > 0);
  }, [props.restCommunicationTime]);

  // 10秒毎に通信状況を取得する
  useEffect(() => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) return;
    const intervalId = setInterval(() => {
      setNow(new Date()); //useEffectが実行される度に、stateを更新し、次のuseEffectを実行する
      // 通信テスト中でない場合は取得しない
      if (isComTestNow) {
        getCommunicationGateway(siteID, props.gwID)
          .then(response => {
            setConnectionInfo(response);
          })
          .catch(err => {
            alert(trans('CommunicationTestDialog.alertMsg.operationErr'));
          });
      }
    }, 10000);

    //10秒毎にタイマを削除
    return function () {
      clearInterval(intervalId);
    };
  }, [siteID, props.gwID, trans, now]);

  /**
   * CANCELボタン押下時処理
   */
  const handleClose = () => {
    props.setIsOpenDialog(false);
  };
  /**
   * 通信テスト開始ボタン押下時処理
   */
  const handleComTest = async () => {
    if (siteID === undefined) return;
    setAPIWait(true);

    try {
      //通信テスト開始時間の更新
      await postCommunicationGateway(siteID, props.gwID);
      props.updateComTestStartTime(props.gwID);
      //通信状況の取得
      const connectionInfo = await getCommunicationGateway(siteID, props.gwID);
      setConnectionInfo(connectionInfo);
    } catch {
      alert(trans('CommunicationTestDialog.alertMsg.operationErr'));
    } finally {
      setAPIWait(false);
    }
  };

  /**
   * 残り時間を表示する文字を作成
   * @returns 残り時間(MM:SS)
   */
  const timeRemainCalc = () => {
    //秒⇒分と秒に加工
    const numSecond = props.restCommunicationTime;
    const minutes = Math.floor(numSecond / 60);
    const second = Math.floor(numSecond % 60);
    //prettier-ignore
    //2桁表示に加工
    return `${String(minutes).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
  };

  /**
   * 通信状態表
   * @param props 通信状態リスト
   * @returns JSX
   */
  const CommunicationStatusList = (props: {
    connectionInfo:
      | apiIF.resParameters.slUnitCommunications
      | apiIF.resParameters.rtUnitCommunications;
  }) => {
    // ユニットがない場合
    if (
      ('sl-system-1' in props.connectionInfo &&
        props.connectionInfo['sl-system-1']['outdoor-unit'].length === 0 &&
        props.connectionInfo['sl-system-1']['indoor-unit'].length === 0 &&
        props.connectionInfo['sl-system-2']['outdoor-unit'].length === 0 &&
        props.connectionInfo['sl-system-2']['indoor-unit'].length === 0) ||
      ('rt-system-1' in props.connectionInfo &&
        props.connectionInfo['rt-system-1']['outdoor-unit'].length === 0 &&
        props.connectionInfo['rt-system-1']['indoor-unit'].length === 0 &&
        props.connectionInfo['rt-system-2']['outdoor-unit'].length === 0 &&
        props.connectionInfo['rt-system-2']['indoor-unit'].length === 0)
    ) {
      return (
        <Grid item xs={12} textAlign={'center'}>
          {trans('CommunicationTestDialog.content.text.noUnit')}
        </Grid>
      );
    }
    // ユニットの接続状況をソート、整形する。
    const unitConnectionInfo: {
      unitId: string;
      unitName: string;
      address: string;
      connectionStatus: 'connected' | 'notConnected';
    }[] = [];
    // SL通信の場合
    if ('sl-system-1' in props.connectionInfo) {
      props.connectionInfo['sl-system-1']['outdoor-unit']
        .sort((a, b) => Number(a['sl-address']) - Number(b['sl-address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['outdoor-id'],
            unitName: unitInfo['outdoor-unit-name'],
            address: unitInfo['sl-address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['sl-system-1']['indoor-unit']
        .sort((a, b) => Number(a['sl-address']) - Number(b['sl-address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['indoor-unit-name'],
            address: unitInfo['sl-address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['sl-system-2']['outdoor-unit']
        .sort((a, b) => Number(a['sl-address']) - Number(b['sl-address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['outdoor-id'],
            unitName: unitInfo['outdoor-unit-name'],
            address: unitInfo['sl-address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['sl-system-2']['indoor-unit']
        .sort((a, b) => Number(a['sl-address']) - Number(b['sl-address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['indoor-unit-name'],
            address: unitInfo['sl-address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
    }

    // RT通信の場合
    if ('rt-system-1' in props.connectionInfo) {
      props.connectionInfo['rt-system-1']['outdoor-unit']
        .sort((a, b) => Number(a['address']) - Number(b['address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['unit-name'],
            address: unitInfo['address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['rt-system-1']['indoor-unit']
        .sort((a, b) => Number(a['address']) - Number(b['address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['indoor-unit-name'],
            address: unitInfo['address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['rt-system-2']['outdoor-unit']
        .sort((a, b) => Number(a['address']) - Number(b['address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['unit-name'],
            address: unitInfo['address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
      props.connectionInfo['rt-system-2']['indoor-unit']
        .sort((a, b) => Number(a['address']) - Number(b['address']))
        .forEach(unitInfo => {
          unitConnectionInfo.push({
            unitId: unitInfo['unit-id'],
            unitName: unitInfo['indoor-unit-name'],
            address: unitInfo['address'],
            connectionStatus: unitInfo['connection-status'],
          });
        });
    }

    return (
      <Grid
        item
        xs={12}
        className="communicationTestDialog__content__list__body">
        {unitConnectionInfo.map(data => (
          <Grid
            container
            alignItems="center"
            key={`${data['unitId']}`}
            className={
              data['connectionStatus'] === 'connected'
                ? 'communicationTestDialog__content__list__body__row--connect'
                : 'communicationTestDialog__content__list__body__row--notConnect'
            }>
            {/* ユニット名 */}
            <Grid
              item
              xs={gridXsUnitName}
              className="communicationTestDialog__content__list__body__row__cell">
              {data['unitName']}
            </Grid>
            {/* アドレス */}
            <Grid
              item
              xs={gridXsAddress}
              className="communicationTestDialog__content__list__body__row__cell">
              {data['address']}
            </Grid>
            {/* 通信状況 */}
            <Grid
              item
              xs={gridXsConnectionStatus}
              className="communicationTestDialog__content__list__body__row__cell">
              {data['connectionStatus'] === 'connected'
                ? trans('CommunicationTestDialog.content.list.connected')
                : trans('CommunicationTestDialog.content.list.notConnected')}
            </Grid>
          </Grid>
        ))}
      </Grid>
    );
  };
  return (
    <>
      <Dialog open={props.isOpenDialog} onClose={handleClose}>
        <DialogTitle>{trans('CommunicationTestDialog.title')}</DialogTitle>
        <DialogContent className="communicationTestDialog__content">
          <Stack spacing={4}>
            {/* 説明文 */}
            <DialogContentText>
              {trans('CommunicationTestDialog.content.msg')}
            </DialogContentText>
            {/* 通信テスト開始ボタン */}
            <Grid container alignItems={'center'} columnSpacing={3}>
              <Grid item xs={4}>
                <PrimaryButton
                  onClick={handleComTest}
                  disabled={isComTestNow}
                  buttonText={
                    isComTestNow
                      ? `${trans(
                          'CommunicationTestDialog.content.button.checking'
                        )}  (${timeRemainCalc()})`
                      : `${trans(
                          'CommunicationTestDialog.content.button.beforeCheck'
                        )}`
                  }
                  id={`mv-btnOkComTest${props.gwID}`}
                  apiWait={apiWait}
                />
              </Grid>
              <Grid item xs={8}>
                *
                {trans('CommunicationTestDialog.content.text.maxTime', {
                  minutes: limit.COMMUNICATION_TEST_TIME / 60,
                })}
              </Grid>
            </Grid>
            {/* 接続状況 */}
            <Grid
              container
              component={Paper}
              className="communicationTestDialog__content__list">
              {/* ヘッダ */}
              <Grid
                item
                xs={gridXsUnitName}
                className="communicationTestDialog__content__list__header">
                {trans('CommunicationTestDialog.content.list.name')}
              </Grid>
              <Grid
                item
                xs={gridXsAddress}
                className="communicationTestDialog__content__list__header">
                {trans('CommunicationTestDialog.content.list.address')}
              </Grid>
              <Grid
                item
                xs={gridXsConnectionStatus}
                className="communicationTestDialog__content__list__header">
                {trans('CommunicationTestDialog.content.list.status')}
              </Grid>
              {isComTestNow ? (
                connectionInfo !== undefined ? (
                  //ユニットの通信状況
                  <CommunicationStatusList connectionInfo={connectionInfo} />
                ) : (
                  // 通信テスト中に通信状況が取得できていない場合
                  <Grid item xs={12} textAlign={'center'}>
                    <CircularProgress size={20} color="info" />
                  </Grid>
                )
              ) : (
                //通信テスト開始前
                <Grid item xs={12} textAlign={'center'}>
                  {trans('CommunicationTestDialog.content.text.beforeTest')}
                </Grid>
              )}
            </Grid>
          </Stack>
        </DialogContent>
        {/* CANCELボタン */}
        <DialogActions>
          <SecondaryButton
            disabled={apiWait}
            onClick={handleClose}
            buttonText={trans('CommunicationTestDialog.actions.cancel')}
            id={`mv-btnCancelComTest${props.gwID}`}
          />
        </DialogActions>
      </Dialog>
      <APIErrorMessage
        open={errorOpen}
        setOpen={setErrorOpen}
        id={`${props.gwID}ComTestErrDlgBtn`}
      />
    </>
  );
};
export default CommunicationTest;
